/**
 * DiscussionThread component
 *
 * Inline collapsible comment thread shown on discussion-type community posts.
 * Only NFT-holding members can read and post comments.
 *
 * Props:
 *  - communityId  : string  — community _id
 *  - contentId    : string  — CommunityContent _id (type must be "discussion")
 *  - isCreator    : bool    — whether the current user is the community creator
 *  - walletAddress: string  — connected wallet (used for membership proof on API)
 *  - commentCount : number  — pre-fetched count shown in the toggle button
 */

import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import { Oval } from "react-loader-spinner";
import {
  BsChatDots,
  BsSend,
  BsTrash,
  BsChevronDown,
  BsChevronUp,
} from "react-icons/bs";

import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import notify from "../common/notify";
import { setShowAuthModal } from "../../redux/slices/generalSlice";
import {
  useGetDiscussionCommentsQuery,
  useAddDiscussionCommentMutation,
  useDeleteDiscussionCommentMutation,
} from "../../redux/slices/api/communityApiSlice";

// ---------------------------------------------------------------------------
// Single comment row
// ---------------------------------------------------------------------------
const CommentRow = ({ comment, isCreator, currentUserId, communityId, contentId }) => {
  const [deleteComment, { isLoading: isDeleting }] = useDeleteDiscussionCommentMutation();

  if (comment.deleted) {
    return (
      <div className="flex gap-3 py-2 opacity-50">
        <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
        <p className="text-sm text-gray-400 italic">[deleted]</p>
      </div>
    );
  }

  const authorName =
    comment.author?.fundername ||
    [comment.author?.firstname, comment.author?.lastname].filter(Boolean).join(" ") ||
    "Member";

  const canDelete =
    isCreator || (currentUserId && comment.author?._id === currentUserId);

  const handleDelete = async () => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteComment({
        id: communityId,
        contentId,
        commentId: comment._id,
      }).unwrap();
    } catch {
      notify("error", "Failed to delete comment");
    }
  };

  return (
    <div className="flex gap-3 py-2.5 group">
      <img
        src={comment.author?.images?.profile || UserPlaceHolderImg}
        alt=""
        className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-sm text-gray-800">{authorName}</span>
          <span className="text-xs text-gray-400">
            {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
          </span>
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 text-xs flex items-center gap-1"
            >
              <BsTrash />
              {isDeleting ? "Deleting…" : "Delete"}
            </button>
          )}
        </div>
        <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap break-words">
          {comment.text}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const DiscussionThread = ({
  communityId,
  contentId,
  isCreator,
  walletAddress,
  commentCount = 0,
  currentUserId,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const textareaRef = useRef(null);
  const dispatch = useDispatch();

  const {
    data,
    isLoading,
    isFetching,
  } = useGetDiscussionCommentsQuery(
    { id: communityId, contentId, walletAddress },
    { skip: !open }
  );

  const [addComment, { isLoading: isPosting }] = useAddDiscussionCommentMutation();

  // Auto-focus textarea when thread opens
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > 2000) {
      notify("error", "Comment must be 2000 characters or less");
      return;
    }
    try {
      await addComment({
        id: communityId,
        contentId,
        text: trimmed,
        walletAddress,
      }).unwrap();
      setText("");
      textareaRef.current?.focus();
    } catch (err) {
      const status = err?.status;
      const msg = err?.data?.message;
      if (status === 401) {
        notify("error", "Please sign in to post a comment");
      } else if (status === 403) {
        notify("error", "Only community members can comment");
      } else {
        notify("error", msg || "Failed to post comment");
      }
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl/Cmd + Enter to submit
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const comments = data?.comments || [];
  const total = data?.pagination?.total ?? commentCount;

  return (
    <div className="mt-3 border-t border-gray-100">
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 mt-3 text-sm text-gray-500 hover:text-primary transition-colors"
      >
        <BsChatDots />
        <span>
          {total > 0
            ? `${total} comment${total !== 1 ? "s" : ""}`
            : "Start the discussion"}
        </span>
        {open ? (
          <BsChevronUp className="ml-1 text-xs" />
        ) : (
          <BsChevronDown className="ml-1 text-xs" />
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-1">
          {/* Loading spinner */}
          {(isLoading || isFetching) && comments.length === 0 && (
            <div className="flex justify-center py-4">
              <Oval height={28} width={28} color="#8538ff" secondaryColor="#8538ff33" />
            </div>
          )}

          {/* Comment list */}
          {comments.length > 0 && (
            <div className="divide-y divide-gray-50">
              {comments.map((c) => (
                <CommentRow
                  key={c._id}
                  comment={c}
                  isCreator={isCreator}
                  currentUserId={currentUserId}
                  communityId={communityId}
                  contentId={contentId}
                />
              ))}
            </div>
          )}

          {!isLoading && comments.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No comments yet — be the first to reply!
            </p>
          )}

          {/* Compose box — for signed-in users and community creators */}
          {(currentUserId || isCreator) ? (
            <>
              <form
                onSubmit={handleSubmit}
                className="flex gap-2 pt-3 items-end"
              >
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Write a comment… (Ctrl+Enter to post)"
                  rows={2}
                  maxLength={2000}
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button
                  type="submit"
                  disabled={isPosting || !text.trim()}
                  className="flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                  title="Post comment"
                >
                  {isPosting ? (
                    <Oval height={16} width={16} color="#fff" secondaryColor="#ffffff44" />
                  ) : (
                    <BsSend />
                  )}
                </button>
              </form>
              <p className="text-xs text-gray-400 text-right mt-1">
                {text.length}/2000
              </p>
            </>
          ) : (
            <p className="pt-3 text-sm text-gray-400 text-center">
              <span
                role="button"
                onClick={() => dispatch(setShowAuthModal(true))}
                className="text-primary hover:underline cursor-pointer font-medium"
              >
                Sign in
              </span>{" "}
              to join the discussion
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscussionThread;
